import { Typography } from '@mui/material';
import {
  faDollar,
  faEye,
  faShuffle,
  faStore,
  faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { HeroMetricProps } from '../../../../../components/Summary/HeroMetric';
import { formatters } from '@utils/formattersV2';
import { Lci3Report } from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { CampaignPulseCard as BaseCampaignPulseCard } from '../../../../../components/Summary/CampaignPulseCard';

type CampaignPulseCardProps = {
  selectedReport: Lci3Report;
  latestSalesReport?: Lci3Report;
};

export const CampaignPulseCard = (props: CampaignPulseCardProps) => {
  const outcomeMetrics: HeroMetricProps[] = [
    ...(props.latestSalesReport?.salesMetrics
      ? ([
          {
            value:
              props.latestSalesReport.salesMetrics.incrementalSalesLift.total,
            valueFormatter: formatters.lift,
            confidenceLevel:
              props.latestSalesReport.salesMetrics.confidenceLevel.total,
            label: 'Incremental Sales Lift',
            colorScheme: 'sales',
            icon: faDollar,
            ['data-testid']: 'incremental-sales-lift-pulse',
          },
          {
            value: props.latestSalesReport.salesMetrics.incrementalSales.total,
            valueFormatter: formatters.currency,
            label: 'Incremental Sales',
            colorScheme: 'sales',
            icon: faDollar,
            ['data-testid']: 'incremental-sales-pulse',
          },
        ] as const)
      : []),
    {
      value: props.selectedReport.visitMetrics.incrementalLift,
      valueFormatter: formatters.lift,
      confidenceLevel:
        props.selectedReport.visitMetrics.confidenceLevel ?? null,
      label: 'Incremental Visit Lift',
      colorScheme: 'visit',
      icon: faStore,
      ['data-testid']: 'incremental-visit-lift-pulse',
    },
    {
      value: props.selectedReport.visitMetrics.incrementalVisits,
      valueFormatter: formatters.visits,
      label: 'Incremental Visits',
      colorScheme: 'visit',
      ['data-testid']: 'incremental-visits-pulse',
      icon: faStore,
    },
  ];

  const exposureMetrics: HeroMetricProps[] = [
    ...(props.latestSalesReport?.salesMetrics
      ? ([
          {
            value:
              props.latestSalesReport.salesMetrics.totalProjectedSales.total,
            valueFormatter: formatters.currency,
            label: 'Total Projected Sales',
            colorScheme: 'sales',
            icon: faDollar,
            size: 'sm',
            ['data-testid']: 'total-projected-sales-mediaOutcomes',
          },
        ] as const)
      : []),
    {
      value: props.selectedReport.visitMetrics.projectedVisits,
      valueFormatter: formatters.visits,
      label: 'Total Projected Visits',
      colorScheme: 'visit',
      icon: faStore,
      size: 'sm',
      ['data-testid']: 'total-projected-visits-mediaOutcomes',
    },
  ];

  const inputMetrics: HeroMetricProps[] = [
    {
      value: props.selectedReport.visitMetrics.impressions,
      valueFormatter: formatters.impressions,
      label: 'Impressions',
      colorScheme: 'plain',
      icon: faEye,
      size: 'sm',
      ['data-testid']: 'impressions-mediaInputs',
    },
    {
      value: props.selectedReport.visitMetrics.uniqueUsers,
      valueFormatter: formatters.users,
      label: 'Unique Users',
      colorScheme: 'plain',
      icon: faUser,
      size: 'sm',
      ['data-testid']: 'unique-users-mediaInputs',
    },
    {
      value: props.selectedReport.visitMetrics.matchedForAttribution,
      valueFormatter: formatters.users,
      label: 'Matched For Attribution',
      colorScheme: 'plain',
      icon: faShuffle,
      size: 'sm',
      ['data-testid']: 'matched-for-attribution-mediaInputs',
    },
  ];

  return (
    <BaseCampaignPulseCard
      incrementalOutcomeMetrics={outcomeMetrics}
      mediaAttributedMetrics={exposureMetrics}
      mediaInputMetrics={inputMetrics}
    >
      <Typography>
        Get a highlight of your campaign Outcomes and Engagement Metrics.
      </Typography>
      <Typography>
        Deep dive into Real-Time Optimization and Campaign Analytics.
      </Typography>
    </BaseCampaignPulseCard>
  );
};
